import {CSSProperties} from 'react'

import Colors from '@utils/Colors.js'

import {hoverOverlay, radius, shadows} from '../styles.js'

// The surfaces (Panel, Card, Divider, DividerHeader, ListItem, ScrollView) take their look from here. Change a value
// here instead of in a component. Surfaces have no outer margins: the space around them comes from the caller's
// style or layout (Stack, Row).

export const surfaceColors = {
    background: Colors.backgroundWhite,
    // A Panel with a shadow lies on the page in the neutral colour
    panelShadowBackground: Colors.backgroundNeutral,
    selectedBackground: Colors.backgroundSelected,
    border: Colors.border,
    divider: Colors.grey30,
    listItemText: Colors.textDark,
    listItemTextInactive: Colors.textMedium,
    listItemHover: hoverOverlay.subtle
}

export const surfaceSizes = {
    cardPadding: 6,
    cardRadius: radius.default,
    dividerHeight: 1,
    dividerHeaderHeight: 36,
    // Between the DividerHeader title row and its content, and between the title and its chevron
    dividerHeaderGap: 6,
    listItem: {
        minHeight: {sm: 32, md: 56},
        paddingX: {sm: 6, md: 12},
        iconSize: {sm: 16, md: 20},
        iconGap: 12,
        // Between the content and the icon buttons at the right
        iconButtonsGap: 6
    }
}

/** Border and shadow of a Panel or Card: a shadow, or a border when it has none */
export function getSurfaceStyle({shadow}: {shadow?: boolean}): CSSProperties {
    return {
        border: shadow ? 'none' : `1px solid ${surfaceColors.border}`,
        boxShadow: shadow ? shadows.raised : undefined
    }
}

/** Background of a ListItem: the hover overlay while hovered or focused (keyboard selection in a list) */
export function getListItemBackground({hovered}: {hovered?: boolean}) {
    return hovered ? surfaceColors.listItemHover : 'transparent'
}

// Card highlight (see highlightCard): pulses the background twice after a short delay
const highlightAnimation = 'ui-card-highlight'
export const highlightClass = 'ui-card-highlighted'
export const highlightDuration = 2500

const styleElementId = 'veloyd-surface-styles'

const css = `
.${highlightClass} {
    animation: ${highlightAnimation} 2s;
    animation-delay: 0.5s;
}

@keyframes ${highlightAnimation} {
    0%, 50%, 100% { background-color: ${surfaceColors.background}; }
    25%, 75% { background-color: ${surfaceColors.selectedBackground}; }
}
`

/** Adds the Card highlight rule and keyframes to the document once (a no-op without a document) */
export function ensureSurfaceStyles() {
    if (typeof document === 'undefined' || document.getElementById(styleElementId)) {
        return
    }

    const style = document.createElement('style')
    style.id = styleElementId
    style.textContent = css
    document.head.appendChild(style)
}
